import {
  Component,
  inject,
  input,
  output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NgButtonComponent } from '@fc-shared/ui/buttons/ng-button';
import { ButtonColor } from '@fc-shared/ui/buttons/ng-button.directive';
import { AppIconsType } from '@fc-shared/ui/icon/icons';
import { MatDialogRef } from '@angular/material/dialog';
import { ModalBottomSheetRef } from '@fc-shared/ui/modal-bottom-sheet/modal-bottom-sheet-ref';
import { TitleCasePipe } from '@angular/common';

@Component({
  selector: 'fc-dialog-footer',
  imports: [NgButtonComponent, TitleCasePipe],
  template: `
    @if (secondaryText()) {
      <ng-button
        [class.w-full]="splitActions()"
        variant="text"
        [color]="secondaryColor()"
        [icon]="secondaryIcon()"
        [disabled]="secondaryDisabled()"
        (click)="secondaryExec($event)"
        >{{ secondaryText() | titlecase }}
      </ng-button>
    }
    @if (haveCancel()) {
      <ng-button
        [class.w-full]="splitActions()"
        variant="outline"
        color="secondary"
        (click)="cancelExec($event)"
      >
        {{ cancelText() | titlecase }}
      </ng-button>
    }
    <ng-button
      [class.w-full]="splitActions()"
      variant="filled"
      [color]="warning() ? 'error' : 'primary'"
      [disabled]="disabled()"
      [loading]="loading()"
      (click)="primaryExec($event)"
      >{{ primaryText() | titlecase }}
    </ng-button>
  `,
  styles: `
    @use 'var' as *;

    :host {
      width: 100%;
      padding: 12px 24px;
      height: 64px;
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;

      @media (max-width: $sm-size) {
        padding: 12px 16px;
      }

      &.full-footer {
        justify-content: flex-start;
        gap: 16px;

        ng-button:first-child {
          margin-right: auto;
        }
      }

      &.split-actions ng-button {
        flex: 1;
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    '[class.full-footer]': 'secondaryText()',
    '[class.split-actions]': 'splitActions()',
  },
})
export class DialogFooterComponent {
  private readonly dialogRef = inject(MatDialogRef, { optional: true });
  private readonly modalBottomSheetRef = inject(ModalBottomSheetRef, {
    optional: true,
  });
  warning = input(false);
  loading = input(false);
  secondaryDisabled = input(false);
  disabled = input(false);
  secondaryText = input<string | null>(null);
  secondaryIcon = input<AppIconsType | null>(null);
  secondaryColor = input<ButtonColor | undefined>(undefined);
  haveCancel = input(true);
  primaryText = input('Save');
  cancelText = input('Cancel');
  readonly splitActions = input(false);
  primaryClicked = output<Event>();
  secondaryClicked = output<Event>();
  closeClicked = output<Event>();

  secondaryFn = input<Function | null>(null);
  primaryFn = input<Function | null>(null);
  cancelFn = input<Function | null>(null);

  primaryExec(event: Event) {
    if (this.loading() || this.disabled()) {
      return;
    }
    const primaryFn = this.primaryFn();
    if (!primaryFn) {
      this.primaryClicked.emit(event);
    } else {
      primaryFn();
    }
  }

  secondaryExec(event: Event) {
    if (this.secondaryDisabled() || this.loading()) {
      return;
    }
    const secondaryFn = this.secondaryFn();
    if (!secondaryFn) {
      this.secondaryClicked.emit(event);
    } else {
      secondaryFn();
    }
  }

  cancelExec(event: Event) {
    const cancelFn = this.cancelFn();
    if (!cancelFn) {
      this.dialogRef?.close();
      this.modalBottomSheetRef?.dismiss();
      this.closeClicked.emit(event);
    } else {
      cancelFn();
    }
  }
}
